<template>
  <div>
    <v-card class="card">
      <v-tabs v-model="tab" class="align-center" density="default" height="64">
        <v-tab ripple value="list-enterprise-customers">Enterprise Customers</v-tab>
        <v-tab ripple value="add-enterprise-customer">Add Enterprise Customer</v-tab>
        <v-spacer></v-spacer>
        <v-text-field
          class="align-self-right mt-0 pt-0"
          append-inner-icon="mdi-magnify"
          v-model="search"
          label="Search"
          :single-line="true"
          hide-details
          variant="outlined"
          density="compact"
          v-if="tab === 'list-enterprise-customers'"
        >
        </v-text-field>
      </v-tabs>

      <v-window v-if="tab === 'list-enterprise-customers'">
        <v-data-table
          :loading="loadingEnterpriseCustomers"
          v-if="enterpriseCustomers"
          :headers="headersSubscriptions"
          :items="enterpriseCustomers"
          :search="search"
          v-model:sort-by="sortBy"
          hover
        >
          <template v-slot:item.payerUserEmail="{ item }">
            <div>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.email }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.startDate="{ item }">
            <div>
              {{ item.startDate ? formatDate(item.startDate) : 'N/A' }}
            </div>
          </template>

          <template v-slot:item.expirationDate="{ item }">
            <div :class="{'text-red': new Date().getTime()>new Date(item.expirationDate).getTime()}">
              {{ formatDate(item.expirationDate) }}
            </div>
          </template>

          <template v-slot:item.payerUserName="{ item }">
            <div>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props: props }">
                  <span class="font-weight-bold" v-bind="props">{{ item.payerUser?.firstname }} {{
                    item.payerUser?.lastname
                  }}</span>
                </template>
                <span>{{ item.payerUser?.id }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.createdAt="{ item }">
            <div>
              {{ formatDate(item.createdAt) }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu
              location="bottom right"
              :offset="[8, 0]"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="text"
                  icon="mdi-dots-vertical"
                />
              </template>

              <v-list density="compact">
                <v-list-item @click="editableCustomer = item" prepend-icon="mdi-pencil">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addEnterpriseMemberDialogData = item" prepend-icon="mdi-plus"
                             :disabled="!canAddMemberForCustomer(item)">
                  <v-list-item-title>Add member</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:no-data>
            <v-alert v-if="search?.length" color="error" icon="mdi-alert">
              Your search for "{{ search }}" found no results.
            </v-alert>
            <div v-else>
              No data available
            </div>
          </template>
        </v-data-table>
      </v-window>
      <v-window v-else-if="tab === 'add-enterprise-customer'">
        <admin-add-enterprise-customer @onDone="tab = 'list-enterprise-customers'"/>
      </v-window>
    </v-card>

    <edit-enterprise-user-dialog
      :is-visible="!!editableCustomer"
      :customer="editableCustomer"
      :is-loading="loadingUpdateEnterpriseCustomer"
      @cancel="editableCustomer = null"
      @save="updateEnterpriseCustomer"
    />

    <admin-add-enterprise-member-dialog
      :is-visible="!!addEnterpriseMemberDialogData"
      :customer="addEnterpriseMemberDialogData"
      :is-loading="loadingAddEnterpriseMember"
      @cancel="addEnterpriseMemberDialogData = null"
      @save="addEnterpriseMember"
    />

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import format from 'date-fns/format'
import EditEnterpriseUserDialog from '@/components/EditEnterpriseUserDialog.vue'
import AdminAddEnterpriseCustomer from '@/components/AdminAddEnterpriseCustomer.vue'
import AdminAddEnterpriseMemberDialog from '@/components/AdminAddEnterpriseMemberDialog.vue'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'

export default {
  components: {
    AdminAddEnterpriseCustomer,
    EditEnterpriseUserDialog,
    AdminAddEnterpriseMemberDialog,
    InfoMessageDialog
  },
  data: () => ({
    tab: 'list-enterprise-customers',
    sortBy: [{ key: 'registrationDate', order: 'desc' }],
    dialog: [],
    search: '',
    expanded: [],
    headersSubscriptions: [
      {
        title: 'Company',
        sortable: true,
        key: 'company'
      },
      {
        title: 'Payer user email',
        sortable: false,
        key: 'payerUserEmail'
      },
      {
        title: 'Payer user name',
        sortable: false,
        key: 'payerUserName'
      },
      {
        title: 'Start Date',
        sortable: true,
        key: 'startDate'
      },
      {
        title: 'Expiration Date',
        sortable: true,
        key: 'expirationDate'
      },
      {
        title: 'Quantity',
        sortable: true,
        key: 'quantity'
      },
      {
        title: 'Created At',
        key: 'createdAt'
      },
      {
        title: 'Actions',
        key: 'actions',
        sortable: false
      }
    ],
    editableCustomer: null,
    addEnterpriseMemberDialogData: null,
    infoMessageDialogData: null
  }),
  computed: {
    ...mapState({
      subscriptions: state => state.subscriptions.subscriptions,
      enterpriseCustomers: state => state.enterpriseCustomers.enterpriseCustomers,
      loadingEnterpriseCustomers: state => state.enterpriseCustomers.loadingEnterpriseCustomers,
      loadingAddEnterpriseMember: state => state.enterpriseCustomers.loadingAddEnterpriseMember,
      loadingUpdateEnterpriseCustomer: state => state.enterpriseCustomers.loadingUpdateEnterpriseCustomer
    }),
    groupedSubscriptions () {
      const grouped = (this.subscriptions || []).reduce((acc, subscription) => {
        const payerId = subscription.payerUser.id

        if (!acc[payerId]) {
          acc[payerId] = {
            id: subscription.payerUser.id,
            payerUser: subscription.payerUser,
            email: subscription.email,
            children: []
          }
        }

        // Add child subscriptions if it's not the parent
        if (subscription.email !== subscription.payerUser.email) {
          acc[payerId].children.push({ email: subscription.email })
        }

        return acc
      }, {})

      return Object.values(grouped)
    }
  },
  methods: {
    canAddMemberForCustomer (customer) {
      const members = this.groupedSubscriptions.find(subscription => subscription.id === customer.payerUser.id)
      return (members.children.length || 0) + 1 < customer.quantity
    },
    formatDate (date) {
      return format(new Date(date), 'MMMM d, yyyy')
    },
    updateEnterpriseCustomer (data) {
      const { id, ...input } = data
      this.$store.dispatch('enterpriseCustomers/updateEnterpriseCustomer', { enterpriseUserId: id, input })

      this.editableCustomer = null
    },
    async addEnterpriseMember (data) {
      const { id, email } = data

      const newEnterpriseMember = {
        email,
        payerUserId: id
      }

      const response = await this.$store.dispatch('enterpriseCustomers/addEnterpriseMember', newEnterpriseMember)
      if (response?.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Enterprise member has been added successfully.' }, { root: true })
        this.$store.dispatch('subscriptions/listSubscriptions')
      } else {
        this.infoMessageDialogData = {
          message: response.message,
          isOpen: true
        }
      }

      this.addEnterpriseMemberDialogData = null
    }
  },
  mounted () {
    this.$store.dispatch('subscriptions/listSubscriptions')
    this.$store.dispatch('enterpriseCustomers/listEnterpriseCustomers')
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  background: #FCFCFC;
  border: 1px solid #FCFCFC;
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
